import { useEffect } from "react";
import { Navigate } from "react-router-dom";

import { useCurrentSession } from "~/api/sessions/hooks/use-session";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { useAppContext } from "~/providers/app";
import { useProfile } from "~/hooks/queries/profiles";
import { useSendWebEvent } from "~/hooks";
import { useAuth } from "~/providers/store/auth";
import { DialogLoading } from "~/components/DialogLoading";
import { T } from "@repo/transifex";
import { DialogError } from "~/components/DialogError";
import { getErrorMessage } from "@repo/system";
import {
  getSellRouteForTill,
  isUnattendedTill
} from "~/utils/tills/is-unattended-till";
import { getFreedomPaySyncPayload } from "~/utils/freedompay";
import { OfflineModal } from "~/components/offline-modal/offline-modal";

import { HomeGrid } from "./HomeGrid";
import { HomeHeader } from "./HomeHeader";

export const HomePage = () => {
  const auth = useAuth();

  const { data: till } = useCurrentTill();
  const { data: session } = useCurrentSession();
  const {
    data: profile,
    status: profileStatus,
    error: profileError
  } = useProfile(auth.profileId);

  const {
    hardware: { printer }
  } = useAppContext();

  const sendWebEvent = useSendWebEvent();

  useEffect(() => {
    if (!till) return;
    const payload = getFreedomPaySyncPayload(till);
    if (!payload) return;

    sendWebEvent({ type: "FREEDOMPAY_TILL_SYNC_REQUEST", payload });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once per till
  }, [till?.id]);

  if (auth.status === "unattended" && isUnattendedTill(till)) {
    return <Navigate to={getSellRouteForTill(till)} replace />;
  }

  if (profileStatus === "pending") {
    return <DialogLoading title={<T _str="Loading profile" />} />;
  }

  if (profileStatus === "error") {
    return (
      <DialogError backUrl="/profiles" error={getErrorMessage(profileError)} />
    );
  }

  // Should never happen - it's handled in __app.route.tsx
  if (!till) return null;

  const isTillOpen = Boolean(till?.sessionId);
  const isSessionValid = Boolean(session?.isValid);
  const hasCashDrawer =
    till?.type !== "kiosk" && Boolean(printer?.hasCashDrawer);

  return (
    <div className="flex h-full flex-1 flex-col gap-3 bg-interface-neutral-1 px-4 py-5">
      <HomeHeader till={till} profileName={profile.name} />

      <HomeGrid
        till={till}
        isTillOpen={isTillOpen}
        isSessionValid={isSessionValid}
        hasCashDrawer={hasCashDrawer}
      />

      <OfflineModal />
    </div>
  );
};
